import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { promoOffers } from '@/mocks/promos';
import { useScrollReveal } from '@/hooks/useScrollReveal';
import { useSavedPromos } from '@/hooks/useSavedPromos';
import { useToast } from '@/hooks/useToast';

const calcTime = (endsAt: string) => {
  const diff = Math.max(0, new Date(endsAt).getTime() - Date.now());
  return {
    d: Math.floor(diff / 86400000),
    h: Math.floor((diff % 86400000) / 3600000),
    m: Math.floor((diff % 3600000) / 60000),
    s: Math.floor((diff % 60000) / 1000),
  };
};

const pad = (n: number) => String(n).padStart(2, '0');

/* ── Inline countdown ── */
const InlineCountdown = ({ endsAt, color }: { endsAt: string; color: string }) => {
  const [time, setTime] = useState(() => calcTime(endsAt));
  useEffect(() => {
    const t = setInterval(() => setTime(calcTime(endsAt)), 1000);
    return () => clearInterval(t);
  }, [endsAt]);

  const units = [
    { val: time.d, label: 'д' },
    { val: time.h, label: 'ч' },
    { val: time.m, label: 'м' },
    { val: time.s, label: 'с' },
  ];

  return (
    <div className="flex items-center gap-1.5">
      {units.map((u, i) => (
        <div key={u.label} className="flex items-center gap-1.5">
          <div
            className="flex flex-col items-center px-2.5 py-1.5 rounded-lg min-w-[38px]"
            style={{ background: `${color}12`, border: `1px solid ${color}30` }}
          >
            <span
              className="font-orbitron font-black text-base leading-none tabular-nums"
              style={{ color, textShadow: `0 0 8px ${color}80` }}
            >
              {pad(u.val)}
            </span>
            <span className="font-mono text-[8px] mt-0.5" style={{ color: 'rgba(255,255,255,0.3)' }}>{u.label}</span>
          </div>
          {i < units.length - 1 && (
            <span className="font-orbitron font-bold text-sm mb-3" style={{ color, opacity: 0.35 }}>:</span>
          )}
        </div>
      ))}
    </div>
  );
};

/* ── Condition item ── */
const ConditionItem = ({ icon, text, color }: { icon: string; text: string; color: string }) => (
  <div className="flex items-start gap-2.5">
    <div
      className="w-5 h-5 flex items-center justify-center rounded-md shrink-0 mt-0.5"
      style={{ background: `${color}15`, border: `1px solid ${color}25` }}
    >
      <i className={`${icon} text-[10px]`} style={{ color }} />
    </div>
    <span className="font-mono text-xs text-white/50 leading-relaxed">{text}</span>
  </div>
);

/* ── Detail card ── */
interface DetailCardProps {
  offer: typeof promoOffers[0];
  index: number;
  isExpanded: boolean;
  onToggle: () => void;
}

const CONDITIONS: Record<number, { icon: string; text: string }[]> = {
  1: [
    { icon: 'ri-capsule-line', text: 'Распространяется на все витамины и БАД в каталоге' },
    { icon: 'ri-shopping-bag-line', text: 'Минимальная сумма заказа — 500 ₽' },
    { icon: 'ri-user-line', text: 'Для всех покупателей, включая новых' },
    { icon: 'ri-coupon-line', text: 'Промокод VIT40 вводится при оформлении заказа' },
    { icon: 'ri-calendar-line', text: 'Акция не суммируется с другими скидками' },
    { icon: 'ri-truck-line', text: 'Доставка оплачивается отдельно по тарифам' },
  ],
  2: [
    { icon: 'ri-heart-3-line', text: 'Кардиомагнил, Аспаркам, Венарус, Троксевазин и другие' },
    { icon: 'ri-shopping-bag-line', text: 'Минимальная сумма заказа — 800 ₽' },
    { icon: 'ri-coupon-line', text: 'Промокод HEART30 вводится при оформлении' },
    { icon: 'ri-calendar-line', text: 'Акция действует до 15 апреля включительно' },
    { icon: 'ri-information-line', text: 'Не распространяется на рецептурные препараты' },
  ],
  3: [
    { icon: 'ri-calendar-2-line', text: 'Скидка действует каждую субботу и воскресенье' },
    { icon: 'ri-price-tag-3-line', text: 'Применяется автоматически, промокод не нужен' },
    { icon: 'ri-store-2-line', text: 'Распространяется на весь каталог без исключений' },
    { icon: 'ri-time-line', text: 'Заказ должен быть оформлен в выходной день' },
    { icon: 'ri-information-line', text: 'Не суммируется с другими акциями и промокодами' },
  ],
};

const INCLUDED_CATEGORIES: Record<number, { label: string; count: number; color: string }[]> = {
  1: [
    { label: 'Витамины A, B, C, D, E', count: 48, color: '#ff2d78' },
    { label: 'Омега-3 и рыбий жир', count: 12, color: '#ff6b00' },
    { label: 'Мультивитамины', count: 24, color: '#f0ff00' },
    { label: 'Минералы и микроэлементы', count: 31, color: '#39ff14' },
  ],
  2: [
    { label: 'Антиагреганты', count: 8, color: '#00f5ff' },
    { label: 'Флеботоники', count: 11, color: '#39ff14' },
    { label: 'Ангиопротекторы', count: 9, color: '#ff2d78' },
    { label: 'Препараты магния', count: 14, color: '#f0ff00' },
  ],
  3: [
    { label: 'Весь каталог', count: 1200, color: '#39ff14' },
    { label: 'Включая новинки', count: 45, color: '#00f5ff' },
    { label: 'Сезонные товары', count: 87, color: '#f0ff00' },
  ],
};

const DetailCard = ({ offer, index, isExpanded, onToggle }: DetailCardProps) => {
  const conditions = CONDITIONS[offer.id] ?? [];
  const categories = INCLUDED_CATEGORIES[offer.id] ?? [];
  const [copied, setCopied] = useState(false);
  const { isSaved, toggleSave } = useSavedPromos();
  const { showToast } = useToast();
  const saved = isSaved(offer.id);

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (offer.code) {
      navigator.clipboard.writeText(offer.code).catch(() => {});
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleSave = (e: React.MouseEvent) => {
    e.stopPropagation();
    const willBeSaved = !saved;
    toggleSave(offer.id);
    if (willBeSaved) {
      showToast({
        type: 'promo_save',
        title: 'Акция сохранена!',
        message: offer.title,
        color: offer.color,
        link: '/profile',
        linkLabel: 'Посмотреть в профиле',
      });
    } else {
      showToast({
        type: 'promo_remove',
        title: 'Акция удалена из закладок',
        message: offer.title,
      });
    }
  };

  return (
    <div
      className="rounded-2xl overflow-hidden transition-all duration-500"
      style={{
        border: isExpanded ? `1px solid ${offer.color}45` : `1px solid ${offer.color}18`,
        background: isExpanded ? `${offer.color}05` : 'rgba(255,255,255,0.02)',
        boxShadow: isExpanded ? `0 0 30px ${offer.color}10` : 'none',
      }}
    >
      {/* Top accent */}
      <div
        className="h-0.5 w-full"
        style={{ background: `linear-gradient(90deg, ${offer.color}, ${offer.colorSecondary}, transparent)` }}
      />

      {/* Header — always visible */}
      <button
        onClick={onToggle}
        className="w-full flex items-start gap-5 p-6 text-left cursor-pointer group"
      >
        {/* Discount circle */}
        <div
          className="w-16 h-16 flex items-center justify-center rounded-2xl font-orbitron font-black text-xl shrink-0 transition-all duration-300"
          style={{
            background: `${offer.color}18`,
            border: `2px solid ${offer.color}`,
            color: offer.color,
            boxShadow: isExpanded ? `0 0 20px ${offer.color}40` : `0 0 10px ${offer.color}20`,
          }}
        >
          {offer.discount}
        </div>

        <div className="flex-1 min-w-0">
          {/* Badge + timer row */}
          <div className="flex items-center gap-3 mb-2 flex-wrap">
            <span
              className="px-2.5 py-0.5 rounded-full font-mono text-[10px] font-bold"
              style={{ background: offer.badgeColor, color: '#050508', boxShadow: `0 0 8px ${offer.badgeColor}` }}
            >
              {offer.badge}
            </span>
            <div className="flex items-center gap-1.5 font-mono text-[10px] text-white/35">
              <i className="ri-time-line" style={{ color: offer.color }} />
              Осталось:
            </div>
            <InlineCountdown endsAt={offer.endsAt} color={offer.color} />
          </div>

          <h3
            className="font-orbitron font-bold text-base text-white leading-snug mb-1 group-hover:opacity-80 transition-opacity"
          >
            {offer.title}
          </h3>
          <p className="font-mono text-xs text-white/40 leading-relaxed line-clamp-2">{offer.description}</p>
        </div>

        {/* Save button */}
        <button
          onClick={handleSave}
          className="w-9 h-9 flex items-center justify-center rounded-xl shrink-0 transition-all duration-200"
          style={{
            background: saved ? `${offer.color}20` : 'rgba(255,255,255,0.04)',
            border: saved ? `1px solid ${offer.color}50` : '1px solid rgba(255,255,255,0.08)',
          }}
          title={saved ? 'Удалить из сохранённых' : 'Сохранить акцию'}
        >
          <i
            className={saved ? 'ri-bookmark-fill' : 'ri-bookmark-line'}
            style={{ color: saved ? offer.color : 'rgba(255,255,255,0.35)', fontSize: 16 }}
          />
        </button>

        {/* Expand arrow */}
        <div
          className="w-9 h-9 flex items-center justify-center rounded-xl shrink-0 transition-all duration-300"
          style={{
            background: isExpanded ? `${offer.color}18` : 'rgba(255,255,255,0.04)',
            border: isExpanded ? `1px solid ${offer.color}40` : '1px solid rgba(255,255,255,0.08)',
            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        >
          <i
            className="ri-arrow-down-s-line text-lg"
            style={{ color: isExpanded ? offer.color : 'rgba(255,255,255,0.35)' }}
          />
        </div>
      </button>

      {/* Expanded content */}
      <div
        style={{
          maxHeight: isExpanded ? '600px' : '0px',
          overflow: 'hidden',
          transition: 'max-height 0.45s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <div
          className="px-6 pb-6"
          style={{ borderTop: `1px solid ${offer.color}15` }}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-5">

            {/* Left: conditions */}
            <div>
              <p className="font-orbitron font-bold text-xs text-white/60 mb-3 flex items-center gap-2">
                <i className="ri-file-list-3-line" style={{ color: offer.color }} />
                Условия акции
              </p>
              <div className="space-y-2.5">
                {conditions.map((c, i) => (
                  <ConditionItem key={i} icon={c.icon} text={c.text} color={offer.color} />
                ))}
              </div>
            </div>

            {/* Right: included categories + CTA */}
            <div className="flex flex-col gap-5">
              <div>
                <p className="font-orbitron font-bold text-xs text-white/60 mb-3 flex items-center gap-2">
                  <i className="ri-price-tag-3-line" style={{ color: offer.color }} />
                  Что входит в акцию
                </p>
                <div className="space-y-2">
                  {categories.map((cat, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between px-3 py-2 rounded-lg"
                      style={{ background: `${cat.color}08`, border: `1px solid ${cat.color}18` }}
                    >
                      <div className="flex items-center gap-2">
                        <div
                          className="w-1.5 h-1.5 rounded-full"
                          style={{ background: cat.color, boxShadow: `0 0 4px ${cat.color}` }}
                        />
                        <span className="font-mono text-xs text-white/60">{cat.label}</span>
                      </div>
                      <span
                        className="font-orbitron font-bold text-xs"
                        style={{ color: cat.color }}
                      >
                        {cat.count.toLocaleString()} шт.
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* CTA row */}
              <div className="flex flex-col gap-2.5">
                <Link
                  to={`/promos/${offer.id}`}
                  className="whitespace-nowrap flex items-center justify-center gap-2 py-3 rounded-xl font-orbitron font-bold text-xs tracking-wider cursor-pointer transition-all duration-300"
                  style={{ background: offer.color, color: '#050508', boxShadow: `0 0 16px ${offer.color}50` }}
                >
                  <i className="ri-shopping-bag-line" />
                  ПЕРЕЙТИ К АКЦИИ
                </Link>
                {offer.code && (
                  <button
                    onClick={handleCopy}
                    className="whitespace-nowrap flex items-center justify-center gap-2 py-3 rounded-xl font-mono text-xs cursor-pointer transition-all duration-300"
                    style={
                      copied
                        ? { background: '#39ff14', color: '#050508', boxShadow: '0 0 12px rgba(57,255,20,0.5)' }
                        : { border: `1px dashed ${offer.color}50`, color: offer.color, background: `${offer.color}06` }
                    }
                  >
                    <i className={copied ? 'ri-check-line' : 'ri-coupon-3-line'} />
                    {copied ? 'Промокод скопирован!' : `Скопировать промокод: ${offer.code}`}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ── Main section ── */
const PromoDetailCards = () => {
  const [expandedId, setExpandedId] = useState<number | null>(1);
  const { ref: headerRef, isVisible: headerVisible } = useScrollReveal({ threshold: 0.1 });

  return (
    <section className="px-6 lg:px-12 pb-16">
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div
          ref={headerRef as React.RefObject<HTMLDivElement>}
          className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8"
          style={{
            opacity: headerVisible ? 1 : 0,
            transform: headerVisible ? 'translateY(0)' : 'translateY(24px)',
            transition: 'opacity 0.6s cubic-bezier(0.22,1,0.36,1), transform 0.6s cubic-bezier(0.22,1,0.36,1)',
          }}
        >
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div
                className="w-1 h-6 rounded-full"
                style={{ background: 'linear-gradient(to bottom, #ff2d78, transparent)' }}
              />
              <span className="font-mono text-xs tracking-widest uppercase" style={{ color: 'rgba(255,45,120,0.7)' }}>
                Текущие акции
              </span>
            </div>
            <h2 className="font-orbitron font-black text-3xl md:text-4xl text-white">
              ДЕТАЛИ<br />
              <span style={{ color: '#ff2d78', textShadow: '0 0 20px rgba(255,45,120,0.4)' }}>ПРЕДЛОЖЕНИЙ</span>
            </h2>
          </div>
          <p className="font-mono text-xs text-white/30 max-w-xs">
            Нажмите на карточку, чтобы увидеть полные условия акции, список товаров и промокод
          </p>
        </div>

        {/* Cards */}
        <div className="space-y-4">
          {promoOffers.map((offer, i) => (
            <div
              key={offer.id}
              style={{
                opacity: headerVisible ? 1 : 0,
                transform: headerVisible ? 'translateY(0)' : 'translateY(24px)',
                transition: `opacity 0.6s cubic-bezier(0.22,1,0.36,1) ${0.1 + i * 0.1}s, transform 0.6s cubic-bezier(0.22,1,0.36,1) ${0.1 + i * 0.1}s`,
              }}
            >
              <DetailCard
                offer={offer}
                index={i}
                isExpanded={expandedId === offer.id}
                onToggle={() => setExpandedId(prev => prev === offer.id ? null : offer.id)}
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PromoDetailCards;
